import { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Check, Edit3 } from "lucide-react";
import DatePickerIT from "./ui/date-picker-it";
import { api } from "../lib/api";

/**
 * Blocco 4 · F · Modal "Modifica azione" (mete row).
 *
 * Props:
 *  - azione: current mete object
 *  - goals: list of active personal_goals
 *  - onClose(): void
 *  - onSaved(updatedMeta): void
 *  - saveFn(patch): Promise<updatedMeta>  — es. api.updateMeta(id, patch)
 */
const LEVELS = [
  { v: "annual",  label: "Anno" },
  { v: "monthly", label: "Mese" },
  { v: "weekly",  label: "Settimana" },
];
const PRIORITIES = [
  { v: "low",      label: "Bassa" },
  { v: "medium",   label: "Media" },
  { v: "high",     label: "Alta" },
  { v: "critical", label: "Critica" },
];

export default function AzioneEditModal({ azione, goals: goalsProp, onClose, onSaved, saveFn }) {
  const [form, setForm] = useState({
    title: azione.title || "",
    description: azione.description || "",
    level: azione.level || "weekly",
    meta_id: azione.meta_id || "",
    area_id: azione.area_id || "",
    priority: azione.priority || "medium",
    deadline_date: azione.deadline_date || "",
  });
  const [goals, setGoals] = useState(goalsProp || []);
  const [areas, setAreas] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!goalsProp) api.listGoals({ status: "active" }).then(setGoals).catch(() => {});
    api.listAreas().then(setAreas).catch(() => {});
  }, [goalsProp]);

  const submit = async () => {
    if (form.title.trim().length < 2) { toast.error("Il titolo deve avere almeno 2 caratteri"); return; }
    setBusy(true);
    try {
      const patch = {};
      if (form.title !== azione.title) patch.title = form.title;
      if (form.description !== (azione.description || "")) patch.description = form.description || null;
      if (form.level !== azione.level) patch.level = form.level;
      if ((form.meta_id || "") !== (azione.meta_id || "")) patch.meta_id = form.meta_id || null;
      if ((form.area_id || "") !== (azione.area_id || "")) patch.area_id = form.area_id || null;
      if (form.priority !== azione.priority) patch.priority = form.priority;
      if (form.deadline_date !== (azione.deadline_date || "")) patch.deadline_date = form.deadline_date || null;
      if (Object.keys(patch).length === 0) { toast("Nessuna modifica"); onClose(); return; }
      const updated = await saveFn(patch);
      toast.success("Azione aggiornata");
      // Chiudi PRIMA di notificare il parent: se onSaved lancia (es.
      // setState sync error, refetch fallito), il modal deve comunque
      // chiudersi — il record è già salvato server-side.
      try { onSaved?.(updated); }
      catch (e2) { console.warn("onSaved callback failed:", e2); }
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento azione");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="azione-edit-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold inline-flex items-center gap-2">
            <Edit3 size={18}/> Modifica azione
          </h3>
          <button onClick={onClose} aria-label="Chiudi"><X size={20}/></button>
        </div>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Titolo *</div>
          <input className="pxos-input" value={form.title} maxLength={300}
                 onChange={(e) => setForm({ ...form, title: e.target.value })}
                 data-testid="azione-edit-title"/>
        </label>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Descrizione</div>
          <textarea className="pxos-input" rows={3} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="azione-edit-description"/>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <div className="opacity-70 mb-1">Livello</div>
            <select className="pxos-input" value={form.level}
                    onChange={(e) => setForm({ ...form, level: e.target.value })}
                    data-testid="azione-edit-level">
              {LEVELS.map((l) => <option key={l.v} value={l.v}>{l.label}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <div className="opacity-70 mb-1">Priorità</div>
            <select className="pxos-input" value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    data-testid="azione-edit-priority">
              {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
            </select>
          </label>
        </div>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Meta collegata</div>
          <select className="pxos-input" value={form.meta_id}
                  onChange={(e) => setForm({ ...form, meta_id: e.target.value })}
                  data-testid="azione-edit-meta">
            <option value="">— nessuna —</option>
            {goals.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <div className="opacity-70 mb-1">Area di vita</div>
            <select className="pxos-input" value={form.area_id}
                    onChange={(e) => setForm({ ...form, area_id: e.target.value })}
                    data-testid="azione-edit-area">
              <option value="">— nessuna —</option>
              {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <div className="opacity-70 mb-1">Scadenza</div>
            <DatePickerIT value={form.deadline_date}
                          onChange={(v) => setForm({ ...form, deadline_date: v })}
                          testId="azione-edit-deadline" placeholder="gg/mm/aaaa"/>
          </label>
        </div>
        <div className="flex gap-2 pt-2 justify-end">
          <button onClick={onClose} className="text-sm underline opacity-70">Annulla</button>
          <button disabled={busy} onClick={submit} className="pxos-btn-primary text-sm" data-testid="azione-edit-submit">
            <Check size={14}/> Salva modifiche
          </button>
        </div>
      </div>
    </div>
  );
}
